import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuotedMarketsActivity, useGateMarket, useFilterMarketIds } from '../api/hooks';
import { useHomebaseModal } from '../components/HomebaseModal';
import {
  showLineBreakdownExpand,
  MarketsSummaryLineRows,
  maxAbsLiabilityFromRows,
  liabilityCellClasses,
} from './marketsLineBreakdown';
import './MarketsPage.css';

const EMPTY_FILTERS = {
  player: '',
  market: '',
  market_id_exact: '',
  league: '',
  min_fees: '',
};

const SORT_OPTIONS = [
  { value: 'main_line_liability', label: 'Main-line liability (max |net|)' },
  { value: 'fees_window', label: 'Fees (Σ)' },
  { value: 'fixture', label: 'Fixture' },
  { value: 'market_player_fixture', label: 'Mkt × player × fixture' },
  { value: 'market', label: 'Market id' },
];

function buildQueryParams(minutes, limit, excludeComplete, applied, sortBy, sortOrder) {
  return {
    minutes,
    limit,
    exclude_complete: excludeComplete,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...(applied.player && { player: applied.player.trim() }),
    ...(applied.market && { market: applied.market.trim() }),
    ...(applied.market_id_exact && { market_id_exact: applied.market_id_exact.trim() }),
    ...(applied.league && { league: applied.league.trim() }),
    ...(applied.min_fees !== '' &&
      applied.min_fees != null && { min_fees_window: parseFloat(applied.min_fees) }),
  };
}

function fmtPct(x) {
  if (x == null || !Number.isFinite(Number(x))) return '—';
  return `${(Number(x) * 100).toFixed(1)}%`;
}

/** 90% CI on fair over probability (same as dashboard quote). */
function fmtCi(lo, hi) {
  const a = lo != null ? Number(lo) : NaN;
  const b = hi != null ? Number(hi) : NaN;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return '—';
  return `${(a * 100).toFixed(1)}–${(b * 100).toFixed(1)}%`;
}

function fmtPx(x) {
  if (x == null || !Number.isFinite(Number(x))) return '—';
  return Number(x).toFixed(2);
}

/** Over decimal; under shown when present (matches API quote_shape_label logic). */
function fmtOddsPair(row) {
  const o = fmtPx(row.marginated_price);
  const uRaw = row.marginated_price_under;
  const uNum = uRaw != null ? Number(uRaw) : NaN;
  const u = Number.isFinite(uNum) && uNum > 0 ? uNum.toFixed(2) : null;
  if (u) return `${o} / ${u}`;
  return o;
}

export default function MarketsSummaryPage() {
  const [minutes] = useState(30);
  const [limit] = useState(100);
  const [excludeComplete, setExcludeComplete] = useState(true);
  const [sortBy, setSortBy] = useState('main_line_liability');
  const [sortOrder, setSortOrder] = useState('desc');
  const [draftFilters, setDraftFilters] = useState(() => ({ ...EMPTY_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_FILTERS }));

  const { data: marketIdOptions = [] } = useFilterMarketIds();

  const queryParams = useMemo(
    () => buildQueryParams(minutes, limit, excludeComplete, appliedFilters, sortBy, sortOrder),
    [minutes, limit, excludeComplete, appliedFilters, sortBy, sortOrder]
  );

  const { data: rows = [], isLoading } = useQuotedMarketsActivity(queryParams);
  const gateMarket = useGateMarket();
  const { confirm, ModalLayer } = useHomebaseModal();

  const liMaxMain = useMemo(() => maxAbsLiabilityFromRows(rows), [rows]);

  const applySearch = useCallback(() => {
    setAppliedFilters({ ...draftFilters });
  }, [draftFilters]);

  const handleFilterChange = useCallback((key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleFilterKeyDown = useCallback(
    (e) => {
      if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
      e.preventDefault();
      applySearch();
    },
    [applySearch]
  );

  const clearFilters = useCallback(() => {
    setDraftFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
  }, []);

  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  const toggleExpand = useCallback((mk) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(mk)) next.delete(mk);
      else next.add(mk);
      return next;
    });
  }, []);

  const runGate = useCallback(
    async (marketKey, payload) => {
      if (!marketKey) return;
      const ok = await confirm({
        title: payload.force_two_sided
          ? 'Two-sided shaping'
          : payload.force_one_sided
            ? 'One-sided shaping'
            : 'Suspend quoting',
        body: payload.force_two_sided
          ? 'Apply two-sided shaping for this market?'
          : payload.force_one_sided
            ? 'Apply one-sided (over-only) shaping for this market?'
            : 'Suspend quoting for this market?',
        danger: !payload.force_two_sided && !payload.force_one_sided,
        confirmLabel: payload.force_two_sided || payload.force_one_sided ? 'Apply' : 'Suspend',
      });
      if (!ok) return;
      await gateMarket.mutateAsync({ marketKey, gatedBy: 'markets-tab', ...payload });
    },
    [gateMarket, confirm]
  );

  return (
    <>
      <div className="page-header">
        <h1>
          Markets · Summary · {minutes}m · top {limit}
        </h1>
        <div className="markets-header-actions">
          <label className="mk-muted" style={{ fontSize: '0.75rem' }}>
            <input
              type="checkbox"
              checked={excludeComplete}
              onChange={(e) => setExcludeComplete(e.target.checked)}
            />{' '}
            Hide complete
          </label>
        </div>
      </div>

      <div
        className="card filter-bar markets-filter-bar"
        title="Filters: Enter or Search. Sort updates live."
      >
        <div className="filter-row markets-filter-row">
          <label className="markets-filter-label">
            Sort
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select markets-filter-select"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="markets-filter-label">
            Order
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="filter-select markets-filter-select"
            >
              <option value="desc">High → low</option>
              <option value="asc">Low → high</option>
            </select>
          </label>
          <label className="markets-filter-label">
            Market (exact)
            <select
              value={draftFilters.market_id_exact}
              onChange={(e) => handleFilterChange('market_id_exact', e.target.value)}
              className="filter-select markets-filter-select markets-filter-select-wide"
              title="Exact market_id; click Search to apply (with other filters)"
            >
              <option value="">All types</option>
              {marketIdOptions.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </label>
          <input
            type="text"
            value={draftFilters.player}
            onChange={(e) => handleFilterChange('player', e.target.value)}
            onKeyDown={handleFilterKeyDown}
            placeholder="Player (contains)"
            className="filter-input markets-filter-input"
          />
          <input
            type="text"
            value={draftFilters.market}
            onChange={(e) => handleFilterChange('market', e.target.value)}
            onKeyDown={handleFilterKeyDown}
            placeholder="Market id (contains)"
            className="filter-input markets-filter-input"
          />
          <input
            type="text"
            value={draftFilters.league}
            onChange={(e) => handleFilterChange('league', e.target.value)}
            onKeyDown={handleFilterKeyDown}
            placeholder="League"
            className="filter-input markets-filter-input"
          />
          <input
            type="number"
            value={draftFilters.min_fees}
            onChange={(e) => handleFilterChange('min_fees', e.target.value)}
            onKeyDown={handleFilterKeyDown}
            placeholder="Min Σ fees"
            min="0"
            step="0.01"
            className="filter-input markets-filter-input"
          />
          <button type="button" className="button button-small" onClick={applySearch}>
            Search
          </button>
          <button type="button" className="button button-small" onClick={clearFilters}>
            Clear
          </button>
        </div>
      </div>

      <div className="card markets-table-wrap">
        {isLoading && rows.length === 0 ? (
          <div className="loading" style={{ padding: '1rem' }}>
            Loading…
          </div>
        ) : (
          <table className="table markets-table">
            <thead>
              <tr>
                <th className="markets-expand-col" aria-label="Line breakdown" />
                <th title="Σ entry fees all lines in window">Σ fee</th>
                <th title="Fees on over legs (all lines)">Fee O</th>
                <th title="Fees on under legs (all lines)">Fee U</th>
                <th title="Liability net over on quote line only">Li O</th>
                <th title="Liability net under on quote line only">Li U</th>
                <th title="Entry leg count all lines">n</th>
                <th title="Fair p (over)">P̂</th>
                <th title="90% CI on fair over probability (p_lower–p_upper)">CI</th>
                <th title="Marginated decimals: over / under when both published">Odds</th>
                <th title="Book count">Bks</th>
                <th title="quote_two_sided: 1w vs 2w; O+U = both over & under decimals">Shape</th>
                <th title="Quote allowed vs suspended">Gate</th>
                <th>Player / mkt / line</th>
                <th>Fixture</th>
                <th>Act</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={16} className="empty-cell mk-muted">
                    No rows (filters or scope).
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const mk = row.market_key;
                  const suspended = Boolean(row.manual_suspend);
                  const gated = !row.gate_allowed;
                  const shape =
                    row.quote_shape_label || (row.quote_two_sided ? '2w' : '1w');
                  const shapeTitle =
                    row.quotes_both_over_under === true
                      ? 'Two-sided quote with over and under marginated prices.'
                      : row.quote_two_sided
                        ? 'Two-sided shape; under decimal missing or zero.'
                        : 'One-sided (over-only) quote shape.';
                  const lb = row.line_breakdown || [];
                  const canExpand = showLineBreakdownExpand(lb, row.line);
                  const open = expandedKeys.has(mk);
                  return (
                    <React.Fragment key={mk}>
                      <tr>
                        <td className="markets-expand-col">
                          {canExpand ? (
                            <button
                              type="button"
                              className="markets-expand-btn"
                              aria-expanded={open}
                              aria-label={open ? 'Hide per-line liability' : 'Show per-line liability'}
                              onClick={() => toggleExpand(mk)}
                            >
                              {open ? '▾' : '▸'}
                            </button>
                          ) : (
                            <span className="mk-muted">·</span>
                          )}
                        </td>
                        <td>{fmtPx(row.fees_window)}</td>
                        <td>{fmtPx(row.fee_over_window)}</td>
                        <td>{fmtPx(row.fee_under_window)}</td>
                        <td>
                          <span className={`markets-line-num-w6 ${liabilityCellClasses(row.liability_net_over_window, liMaxMain)}`}>
                            {fmtPx(row.liability_net_over_window)}
                          </span>
                        </td>
                        <td>
                          <span className={`markets-line-num-w6 ${liabilityCellClasses(row.liability_net_under_window, liMaxMain)}`}>
                            {fmtPx(row.liability_net_under_window)}
                          </span>
                        </td>
                        <td>{row.n_entries_window != null ? row.n_entries_window : '—'}</td>
                        <td>{fmtPct(row.p_fair)}</td>
                        <td className="mk-muted" title="90% interval on fair over prob">
                          {fmtCi(row.p_lower, row.p_upper)}
                        </td>
                        <td className="mk-muted">{fmtOddsPair(row)}</td>
                        <td>{row.n_books != null ? row.n_books : '—'}</td>
                        <td className="mk-muted" title={shapeTitle}>
                          {shape}
                        </td>
                        <td>
                          {suspended ? (
                            <span className="markets-badge markets-badge--sus">Sus</span>
                          ) : gated ? (
                            <span className="markets-badge markets-badge--gate">Off</span>
                          ) : (
                            <span className="markets-badge markets-badge--open">On</span>
                          )}
                        </td>
                        <td className="mk-pwrap">
                          <span className="mk-muted">{row.league || '—'} · </span>
                          {row.player_name || '—'} · {row.market_id || '—'} · L
                          {row.line != null ? row.line : '—'}
                        </td>
                        <td className="mk-pwrap mk-muted">
                          {row.fixture_home_team && row.fixture_away_team
                            ? `${row.fixture_home_team} v ${row.fixture_away_team}`
                            : '—'}
                        </td>
                        <td>
                          <div className="markets-actions">
                            <button
                              type="button"
                              disabled={gateMarket.isPending}
                              onClick={() => runGate(mk, {})}
                            >
                              Sus
                            </button>
                            <button
                              type="button"
                              disabled={gateMarket.isPending}
                              onClick={() => runGate(mk, { force_two_sided: true })}
                            >
                              2w
                            </button>
                            <button
                              type="button"
                              disabled={gateMarket.isPending}
                              onClick={() => runGate(mk, { force_one_sided: true })}
                            >
                              1w
                            </button>
                            <Link className="markets-link" to={`/dashboard?market=${encodeURIComponent(mk)}`}>
                              →
                            </Link>
                          </div>
                        </td>
                      </tr>
                      {canExpand && open ? (
                        <MarketsSummaryLineRows
                          quoteLine={row.line}
                          lineBreakdown={lb}
                          fmtPx={fmtPx}
                        />
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
      <ModalLayer />
    </>
  );
}
